/**
 * Mix-in that extends a given `i18next` language detector, overriding its `detect()` method
 * to map detected languages to the closest matching ones supported by the application.
 * 
 * @author Nick Chevsky <nchevsky@ibm.com>
 * 
 * @param LanguageDetector - Constructor of an `i18next` language detector class to extend.
 * @returns Constructor of the extended language detector class.
 */
function extended (LanguageDetector) {
  return class extends LanguageDetector {
    options = {};
    services;

    /**
     * Maps the language tag(s) detected by the superclass' `detect()` method to the closest matching
     * language tag(s) that are supported by the application.
     * 
     * @param parameters - Arguments to be passed through to `i18next-http-middleware`; not applicable
     *                     to `i18next-browser-languageDetector`.
     * @returns Detected language tag(s) supported by the application.
     */
    detect() {
      let detectedTags;

      // if we are extending `i18next-http-middleware`'s language detector
      if (arguments.length) {
        // this hack keeps `i18next-http-middleware` from overstepping its responsibilities and
        // attempting its own language mapping/fallback before telling us what it detected
        const getBestMatchFromCodes = this.services.languageUtils.getBestMatchFromCodes;
        this.services.languageUtils.getBestMatchFromCodes = undefined;
        try {
          detectedTags = super.detect(...arguments);
        } finally {
          this.services.languageUtils.getBestMatchFromCodes = getBestMatchFromCodes;
        }
        // if we are extending `i18next-browser-languageDetector`
      } else {
        detectedTags = super.detect();
      }
      return detectedTags && this.mapLanguageTags(detectedTags);
    }
    init(services, detectorOptions, i18nextOptions) {
      var _super$init;
      this.options = i18nextOptions;
      this.services = services;
      return (_super$init = super.init) === null || _super$init === void 0 ? void 0 : _super$init.call(this, services, detectorOptions, i18nextOptions);
    }

    /**
     * Maps a given language tag to the closest matching language supported by the application, if any.
     * If i18next's `supportedLngs` option has been explicitly undefined by the application, this function
     * cannot do much and will in most cases return the given tag as-is—except in the case of Chinese,
     * where it is still able to perform some basic mapping from region/script to simplified/traditional.
     * 
     * @param tag - Original language tag as detected by the superclass (e.g. _'zh-Hans-SG'_).
     * @returns Tag of the closest matching language supported by the application (e.g. _'zh-CN'_),
     *          or `undefined` if `tag` is invalid or represents an unsupported language.
     */
    mapLanguageTag(tag) {
      // attempt to parse the given language tag
      let locale;
      try {
        locale = new Intl.Locale(tag);
      } catch {
        return;
      }

      // if the language is Chinese, use its region and/or script to map it to traditional (TW) or simplified (CN)
      if (locale.language == 'zh') {
        const region = ['HK', 'MO', 'TW'].includes(locale.region ?? '') || locale.script == 'Hant' ? 'TW' : 'CN';
        tag = `${locale.language}-${region}`;
      }

      // if we know our supported languages, map the given tag to the closest match
      if (Array.isArray(this.options.supportedLngs)) {
        // if the tag is an exact match for one of our supported locales, return it as-is
        if (this.options.supportedLngs.includes(tag)) {
          return tag;
        }
        // if there was no exact hit but the tag matches a supported base language, return its bare language code
        if (this.options.supportedLngs.includes(locale.language)) {
          return locale.language;
        }
        // if the tag does not match any supported language+region or base language but we do support a different
        // regional variation of the same language, return it as a last resort
        return this.options.supportedLngs.find(supportedTag => supportedTag.replace(/-.+/, '') == locale.language);
      }

      // if supportedLngs has been undefined by the application, return the tag as-is and allow i18next's
      // standard fallback mechanism to do its thing
      return tag;
    }

    /**
     * Maps the given language tag(s) to the closest matching language(s) supported by the application.
     * If i18next's `debug` option is set, also logs—at DEBUG level—the sets of detected/requested languages,
     * application-declared supported languages, and resulting mapped/honored languages.
     * 
     * @param tagOrTags - Original language tag(s) as detected by the superclass (e.g. _'en-CA'_ or
     *                    `['en-CA', 'xx-XX', 'zh-Hans-SG']`).
     * @returns Tag(s) of the closest matching language(s) supported by the application (e.g. _'en'_ or
     *          `['en', 'zh-CN']`), an empty array if no matches were found and `tagOrTags` is an array, or
     *          `undefined` if no matches were found and `tagOrTags` is a string.
     */
    mapLanguageTags(tagOrTags) {
      // map given tag(s)
      const mappedTagOrTags = Array.isArray(tagOrTags) ? tagOrTags.reduce((tags, tag) => {
        const mappedTag = this.mapLanguageTag(tag);
        if (mappedTag) tags.push(mappedTag);
        return tags;
      }, []) : this.mapLanguageTag(tagOrTags);

      // log debugging information
      if (this.options.debug && tagOrTags) {
        console.group('[@console/i18n] Language mappings:');
        console.debug('• Input:', tagOrTags);
        console.debug('• Supported:', Array.isArray(this.options.supportedLngs) ? this.options.supportedLngs : 'N/A; i18next option `supportedLngs` is undefined');
        console.debug('• Output:', mappedTagOrTags);
        console.groupEnd();
      }
      return mappedTagOrTags;
    }
  };
}

export { extended as default };
